<script setup lang="ts">
import StatusPill from '../components/StatusPill.vue'
import { NEUTRAL_PILL_CLASS } from '../composables/statusPillColors'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import type { CvVersion, CvVersionStatus } from '../stores/cv_versions'

// One row per generated CV version, newest first. Selecting a row is the
// parent's concern; this list only reports the click.
defineProps<{
  versions: CvVersion[]
  selectedId: number | null
}>()

const emit = defineEmits<{ select: [id: number] }>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

const STATUS_CLASS: Record<CvVersionStatus, string> = {
  draft: 'bg-amber-500/15 text-amber-300/90',
  approved: 'bg-emerald-500/15 text-emerald-300/90',
  superseded: NEUTRAL_PILL_CLASS,
}

function tokens(v: CvVersion): string {
  if (v.input_tokens == null || v.output_tokens == null) return ''
  return `${v.input_tokens.toLocaleString()} in / ${v.output_tokens.toLocaleString()} out`
}
</script>

<template>
  <ul class="list-none p-0 m-0 flex flex-col gap-2" data-testid="cv-version-list">
    <li v-for="v in versions" :key="v.id">
      <button
        type="button"
        class="w-full text-left rounded border px-3 py-2 flex flex-col gap-1 transition-colors"
        :class="v.id === selectedId ? 'border-white/40 bg-white/10' : 'border-white/10 bg-black/20 hover:bg-white/5'"
        :data-testid="`cv-version-${v.id}`"
        :aria-pressed="v.id === selectedId"
        @click="emit('select', v.id)"
      >
        <span class="flex items-center justify-between gap-3">
          <span class="text-sm text-white">Version {{ v.id }}</span>
          <StatusPill
            :label="v.status"
            :color-class="STATUS_CLASS[v.status]"
            :data-testid="`cv-version-status-${v.id}`"
          />
        </span>
        <span class="text-xs text-white/50 flex flex-wrap gap-x-3">
          <span v-if="v.generated_at" :title="formatAbsoluteDate(v.generated_at)">
            Generated {{ formatDate(v.generated_at, now) }}
          </span>
          <span v-if="v.model" class="font-mono">{{ v.model }}</span>
          <span v-if="tokens(v)">{{ tokens(v) }}</span>
          <span>{{ v.citations.length }} source {{ v.citations.length === 1 ? 'page' : 'pages' }}</span>
        </span>
      </button>
    </li>
  </ul>
</template>
